import { AppRoutes } from '../../../common/constants';
import { Box } from '@mui/material';
import { buildFnaParties } from '../../hooks/useFnaPartiesUpdater';
import { EditFnaStepComponent } from '../types';
import { FNADetails, FnaPolicyStructure, NeedCapacityType } from '../../../common/types/analyses';
import { Icon } from '@iconify/react';
import { JSX } from 'react';
import { LoadingButton } from '@mui/lab';
import { useIsFnaQuoted } from '../../hooks/useIsFnaQuoted';
import { useNavigate } from 'react-router-dom';
import { useUpdateFna } from '../../../common/components/mutations';
import InformationBlock from '../../../common/components/informationBlock';

enum FnaNeeds {
  keyperson = 'keyperson',
  suretyship = 'suretyship',
  buyandsell = 'buy-and-sell',
}

const FnaNeedToFnaPartiesField: Record<FnaNeeds, keyof FNADetails> = {
  [FnaNeeds.keyperson]: 'keypersonParties',
  [FnaNeeds.suretyship]: 'suretyshipParties',
  [FnaNeeds.buyandsell]: 'buyandsellParties',
};

interface NeedSectionActionButtonProps {
  /**
   * Optional callback when the start button is clicked
   */
  onClickStart?: () => void;
  /**
   * Optional callback when the update button is clicked
   */
  onClickUpdate?: () => void;
  /**
   * Optional callback when the delete button is clicked
   */
  onClickDelete?: () => void;
  /**
   * Optional callback when the results button is clicked
   */
  onClickResults?: () => void;
  /**
   * Optional callback when the quotes button is clicked
   */
  onClickQuotes?: () => void;
  /**
   * Indicates if the action button is loading
   */
  isLoading: boolean;
}

/**
 * Action button for the need section
 */
const NeedSectionActionButton = ({
  isLoading,
  onClickStart,
  onClickUpdate,
  onClickDelete,
  onClickResults,
  onClickQuotes,
}: NeedSectionActionButtonProps): JSX.Element => (
  <Box className="flex space-x-2">
    {onClickStart && (
      <LoadingButton loading={isLoading} onClick={onClickStart} variant="outlined">
        Start
      </LoadingButton>
    )}
    {onClickDelete && (
      <LoadingButton loading={isLoading} onClick={onClickDelete} variant="outlined">
        <Icon width={20} icon="ic:outline-delete" />
      </LoadingButton>
    )}
    {onClickUpdate && (
      <LoadingButton loading={isLoading} onClick={onClickUpdate} variant="outlined">
        Update
      </LoadingButton>
    )}
    {onClickQuotes && (
      <LoadingButton loading={isLoading} onClick={onClickQuotes} variant="outlined">
        Quoted
      </LoadingButton>
    )}
    {onClickResults && (
      <LoadingButton loading={isLoading} onClick={onClickResults} variant="contained">
        Results
      </LoadingButton>
    )}
  </Box>
);

/**
 * Step with FNA needs
 */
const NeedsStep: EditFnaStepComponent = ({ fna }): JSX.Element => {
  const navigate = useNavigate();
  const { mutateAsync, isPending: mutationIsLoading } = useUpdateFna();
  const { isBuyAndSellQuoted, isKeypersonQuoted, isSuretyshipQuoted } = useIsFnaQuoted(fna.id);

  /**
   * Initiate the target needs before leading to the needs edit page
   */
  const handleStartNeeds = async (type: FnaNeeds): Promise<void> => {
    let updatedFna = fna;
    // If FNA doesn't have the "buyAndSell" object, create it before
    if (!fna.buyandsell) {
      updatedFna = await mutateAsync({
        ...fna,
        buyandsell: {
          fnaid: fna.id,
          policyStructureID: FnaPolicyStructure.Traditional,
        },
      });
    }

    await mutateAsync(
      {
        ...updatedFna,
        // Apply default parties to the FNA
        ...(!(updatedFna[FnaNeedToFnaPartiesField[type]] as Array<unknown> | undefined)?.length && {
          [FnaNeedToFnaPartiesField[type]]: buildFnaParties(
            updatedFna.parties,
            type === FnaNeeds.buyandsell
              ? {
                  fnaBuyandsellID: updatedFna.buyandsell?.id,
                  capacityID: NeedCapacityType.BuyingAndSelling,
                }
              : undefined,
          ),
        }),
      },
      {
        onSuccess: (): void => {
          navigate(`${AppRoutes.FNAs}/${fna.id}/${type}`);
        },
      },
    );
  };

  /**
   * Delete the needs parties when the delete button is clicked
   */
  const handleDeleteNeeds = async (type: FnaNeeds): Promise<void> => {
    await mutateAsync({
      ...fna,
      [FnaNeedToFnaPartiesField[type]]: [],
    });
  };

  /**
   * just navigate to the needs edit page when the update button is clicked
   */
  const handleUpdateNeeds = (type: FnaNeeds): void => {
    navigate(`${AppRoutes.FNAs}/${fna.id}/${type}`);
  };

  /**
   * Navigate to the needs results page when the results button is clicked
   */
  const handleNeedResults = (type: FnaNeeds): void => {
    navigate(`${AppRoutes.FNAs}/${fna.id}/${type}/needs`);
  };

  const handleNeedQuotes = (): void => {
    navigate(`${AppRoutes.FNAs}/${fna.id}/quotes`);
  };

  return (
    <Box className="space-y-4">
      <InformationBlock
        active={fna.isSuretyship}
        subTitle={
          fna.suretyshipCount > 0
            ? `${fna.suretyshipCount} needs identified`
            : 'No needs identified'
        }
        title="Suretyship protection (Contingent Liability)"
        description="The use of insurance to settle business debts for which the owner/s signed a personal guarantee."
        action={
          <NeedSectionActionButton
            isLoading={mutationIsLoading}
            onClickStart={
              fna.isSuretyship || isSuretyshipQuoted
                ? undefined
                : (): unknown => handleStartNeeds(FnaNeeds.suretyship)
            }
            onClickUpdate={
              fna.isSuretyship && !isSuretyshipQuoted
                ? (): void => handleUpdateNeeds(FnaNeeds.suretyship)
                : undefined
            }
            onClickDelete={
              fna.isSuretyship && !isSuretyshipQuoted
                ? (): unknown => handleDeleteNeeds(FnaNeeds.suretyship)
                : undefined
            }
            onClickQuotes={fna.isSuretyship && isSuretyshipQuoted ? handleNeedQuotes : undefined}
            onClickResults={
              fna.isSuretyship ? (): void => handleNeedResults(FnaNeeds.suretyship) : undefined
            }
          />
        }
      />
      <InformationBlock
        active={fna.isKeyperson}
        subTitle={
          fna.keypersonCount > 0 ? `${fna.keypersonCount} needs identified` : 'No needs identified'
        }
        title="Key person cover"
        description="Insurance against the financial impact that the loss of important staff members causes"
        action={
          <NeedSectionActionButton
            isLoading={mutationIsLoading}
            onClickStart={
              fna.isKeyperson || isKeypersonQuoted
                ? undefined
                : (): unknown => handleStartNeeds(FnaNeeds.keyperson)
            }
            onClickUpdate={
              fna.isKeyperson && !isKeypersonQuoted
                ? (): void => handleUpdateNeeds(FnaNeeds.keyperson)
                : undefined
            }
            onClickDelete={
              fna.isKeyperson && !isKeypersonQuoted
                ? (): unknown => handleDeleteNeeds(FnaNeeds.keyperson)
                : undefined
            }
            onClickQuotes={fna.isKeyperson && isKeypersonQuoted ? handleNeedQuotes : undefined}
            onClickResults={
              fna.isKeyperson ? (): void => handleNeedResults(FnaNeeds.keyperson) : undefined
            }
          />
        }
      />
      <InformationBlock
        active={fna.isBuyandsell}
        subTitle={
          fna.buyandsellCount > 0
            ? `${fna.buyandsellCount} needs identified`
            : 'No needs identified'
        }
        title="Business continuity (Buy-and-Sell)"
        description="A contractual agreement between a business owner and a willing buyer that ensures the continuation of the business and that the owner's heirs receive a fair market value."
        action={
          <NeedSectionActionButton
            isLoading={mutationIsLoading}
            onClickStart={
              fna.isBuyandsell || isBuyAndSellQuoted
                ? undefined
                : (): unknown => handleStartNeeds(FnaNeeds.buyandsell)
            }
            onClickUpdate={
              fna.isBuyandsell && !isBuyAndSellQuoted
                ? (): void => handleUpdateNeeds(FnaNeeds.buyandsell)
                : undefined
            }
            onClickDelete={
              fna.isBuyandsell && !isBuyAndSellQuoted
                ? (): unknown => handleDeleteNeeds(FnaNeeds.buyandsell)
                : undefined
            }
            onClickQuotes={fna.isBuyandsell && isBuyAndSellQuoted ? handleNeedQuotes : undefined}
            onClickResults={
              fna.isBuyandsell ? (): void => handleNeedResults(FnaNeeds.buyandsell) : undefined
            }
          />
        }
      />
    </Box>
  );
};

export default NeedsStep;
